import type { UIMessage } from "ai";
import { splitUserTextForAttachments } from "./chat-attachment-marker";
import { inspectKnowledgeSearchMessage } from "./knowledge-search-message";
import { inspectWebResearchMessage } from "./web-research-message";

function uiMessageTextParts(message: UIMessage): string[] {
  if (!Array.isArray(message.parts)) return [];
  return message.parts.flatMap((part) =>
    part.type === "text" && typeof part.text === "string" && part.text.trim().length > 0
      ? [part.text]
      : [],
  );
}

function fileNamesForCopy(message: UIMessage): string[] {
  if (!Array.isArray(message.parts)) return [];
  return message.parts.flatMap((part) =>
    part.type === "file" && typeof part.filename === "string" && part.filename.length > 0
      ? [part.filename]
      : [],
  );
}

function sourcesBlockForAssistant(message: UIMessage): string | null {
  const knowledge = inspectKnowledgeSearchMessage(message);
  if (knowledge.citations.length > 0) {
    const lines = knowledge.citations.map((citation, index) => {
      const page = citation.page ? ` (p. ${citation.page})` : "";
      return `${index + 1}. ${citation.documentTitle}${page}`;
    });
    return `Sources:\n${lines.join("\n")}`;
  }

  const research = inspectWebResearchMessage(message);
  if (research.sources.length > 0) {
    const lines = research.sources.map((source, index) => {
      const domain = source.domain ? ` — ${source.domain}` : "";
      return `${index + 1}. ${source.title}${domain}${source.url ? `\n   ${source.url}` : ""}`;
    });
    return `Sources:\n${lines.join("\n")}`;
  }

  return null;
}

/**
 * Build clipboard text from the AI SDK UIMessage (same source as bubble display).
 */
export function buildSupervisorMessageCopyText(
  message: UIMessage,
  variant: "user" | "assistant",
): string {
  if (variant === "user") {
    const fromParts = fileNamesForCopy(message);
    const { attachments, visibleTexts } = splitUserTextForAttachments(uiMessageTextParts(message));
    const names = [
      ...fromParts,
      ...attachments.map((item) => item.filename),
    ].filter((name, index, all) => all.indexOf(name) === index);
    const lines = [
      ...names.map((name) => `Attached file: ${name}`),
      ...visibleTexts,
    ];
    return lines.join("\n\n").trim();
  }

  const body = uiMessageTextParts(message).join("\n\n").trim();
  const sources = sourcesBlockForAssistant(message);
  if (body && sources) return `${body}\n\n${sources}`;
  if (sources) return sources;
  return body;
}
